import { useState, Fragment } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
} from '@mui/material'
import {
  Close,
  ExpandMore,
  ExpandLess,
  Circle,
  FiberManualRecord,
} from '@mui/icons-material'

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatPropertyName = (key) =>
  key.replace(/\[(\d+)\]/g, '.$1')
     .split(/[_.]/g)
     .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
     .join(' ')

const shouldHide = (value) => value === null || value === '' || value === 0

const primitiveValue = (value) =>
  typeof value === 'string' ? value : String(value)

// ─── System field filter ──────────────────────────────────────────────────────

const isSystemField = (key) => /^id$|_id$|_uuid$|transaction_id/i.test(key)

const getDocumentLabel = (item, index) => {
  const labelKeys = ['document_number', 'invoice_number', 'doc_number', 'number', 'reference', 'ref']
  for (const k of labelKeys) {
    if (item[k]) return String(item[k])
  }
  return `Item ${index + 1}`
}

// ─── Data Table ───────────────────────────────────────────────────────────────

const ObjectRows = ({ obj, indent = false }) =>
  Object.entries(obj)
    .filter(([, v]) => !shouldHide(v) && typeof v !== 'object')
    .map(([k, v]) => (
      <TableRow key={k}>
        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold', py: 0.5, pl: indent ? 3 : 1.5 }}>
          {formatPropertyName(k)}
        </TableCell>
        <TableCell sx={{ fontSize: '0.7rem', fontFamily: 'monospace', py: 0.5, wordBreak: 'break-word' }}>
          {primitiveValue(v)}
        </TableCell>
      </TableRow>
    ))

const DataTable = ({ data }) => {
  const isDark = useTheme().palette.mode === 'dark'
  if (!data || Object.keys(data).length === 0) return null
  const entries = Object.entries(data).filter(([, v]) => !shouldHide(v))
  if (entries.length === 0) return null

  const sectionHeaderSx = {
    fontSize: '0.7rem', fontWeight: 'bold',
    bgcolor: isDark ? 'grey.800' : 'grey.100',
    py: 0.5, px: 1.5, borderTop: 1, borderColor: 'divider',
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
      <Table size="small">
        <TableBody>
          {entries.map(([key, value]) => {
            const isObjArray = Array.isArray(value) && value.length > 0 && typeof value[0] === 'object'
            const isObj = !Array.isArray(value) && typeof value === 'object' && value !== null
            const isSimpleArray = Array.isArray(value) && !isObjArray

            if (isObjArray) {
              return value.map((item, i) => (
                <Fragment key={`${key}-${i}`}>
                  <TableRow>
                    <TableCell colSpan={2} sx={sectionHeaderSx}>
                      {formatPropertyName(key)} {value.length > 1 ? i + 1 : ''}
                    </TableCell>
                  </TableRow>
                  <ObjectRows obj={item} indent />
                </Fragment>
              ))
            }
            if (isObj) {
              return (
                <Fragment key={key}>
                  <TableRow>
                    <TableCell colSpan={2} sx={sectionHeaderSx}>{formatPropertyName(key)}</TableCell>
                  </TableRow>
                  <ObjectRows obj={value} indent />
                </Fragment>
              )
            }
            return (
              <TableRow key={key}>
                <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold', py: 0.5, pl: 1.5, width: '40%' }}>
                  {formatPropertyName(key)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', fontFamily: 'monospace', py: 0.5, wordBreak: 'break-word' }}>
                  {isSimpleArray ? value.join(', ') : primitiveValue(value)}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// ─── Collapsible Invoice Item ─────────────────────────────────────────────────

const CollapsibleItem = ({ label, data }) => {
  const [open, setOpen] = useState(false)
  const isDark = useTheme().palette.mode === 'dark'
  const entries = Object.entries(data).filter(([k, v]) => !shouldHide(v) && !isSystemField(k) && typeof v !== 'object')

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: 0.75, cursor: 'pointer', userSelect: 'none',
          bgcolor: isDark ? 'grey.800' : 'grey.100',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
        {open ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
      </Box>
      <Collapse in={open}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {entries.map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold', py: 0.5, width: '40%' }}>
                    {formatPropertyName(k)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', fontFamily: 'monospace', py: 0.5, wordBreak: 'break-word' }}>
                    {primitiveValue(v)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  )
}

// ─── Fact-capture timeline helpers ───────────────────────────────────────────

// Collect and sort logs from parent ticket + all child tickets
const getAllLogs = (ticket) => {
  const parent = ticket?.logs || []
  const children = (ticket?.child_tickets || []).flatMap(c => c.logs || [])
  return [...parent, ...children].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
}

// Build an ordered list of fact items from logs + conversation thread (for handbacks)
// Parse "Key Value; Key2 Value2" strings into objects for collapsible display.
// Each segment is split at the last space: everything before = key, last token = value.
// Returns null if any value looks like a plain English word (prose, not structured data).
const parseSemicolonKV = (str) => {
  const parts = str.split(';').map(s => s.trim()).filter(Boolean)
  if (parts.length < 2) return null
  const entries = parts.map(part => {
    const lastSpace = part.lastIndexOf(' ')
    if (lastSpace === -1) return null
    const key = part.slice(0, lastSpace).trim()
    const val = part.slice(lastSpace + 1).trim().replace(/[.,;]+$/, '') // strip trailing punctuation
    // Plain lowercase word (no digits, not all-caps) = prose sentence, not a KV value
    if (/^[a-z]{3,}$/.test(val)) return null
    return [key, val]
  })
  if (entries.some(e => !e)) return null
  return Object.fromEntries(entries)
}

const buildFactList = (ticket) => {
  const logs = getAllLogs(ticket)

  const items = logs.flatMap(log => {
    if (log.function_name === 'capture_fact') {
      const { label, value } = log.parameters || {}
      if (!label) return []
      let parsed = null
      try {
        const p = JSON.parse(value)
        if (p && typeof p === 'object') parsed = p
      } catch {
        // Try converting Python-style dict {key: val} → {"key": val} before falling back
        try {
          const fixed = value.replace(/([{,]\s*)(\w+)\s*:/g, '$1"$2":')
          const p = JSON.parse(fixed)
          if (p && typeof p === 'object') parsed = p
        } catch {
          parsed = parseSemicolonKV(value)
        }
      }
      return [{ type: 'fact', label, value: value || '', parsed, timestamp: log.timestamp }]
    }
    return []
  })

  return items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
}


// ─── Fact Entry ───────────────────────────────────────────────────────────────

const FactEntry = ({ item, isLast }) => {
  const [open, setOpen] = useState(false)
  const isMultiValue = item.parsed !== null && typeof item.parsed === 'object'

  // Build rows for multi-value collapsed display
  const rows = isMultiValue
    ? Object.entries(item.parsed).map(([k, v]) => ({ key: k, val: String(v) }))
    : []

  return (
    <Box
      sx={{
        display: 'flex', gap: 1.5, position: 'relative',
        ...(!isLast && {
          '&::before': {
            content: '""', position: 'absolute',
            left: 6, top: 20, bottom: -8, width: '1px', bgcolor: 'divider',
          },
        }),
      }}
    >
      <Box sx={{ flexShrink: 0, mt: 0.5 }}>
        <FiberManualRecord sx={{ fontSize: 14, color: 'primary.main' }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, pb: 1 }}>
        {isMultiValue ? (
          // Multi-value: collapsible
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Box
              onClick={() => setOpen(o => !o)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 1, py: 0.5, cursor: 'pointer', userSelect: 'none',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                {item.label}
              </Typography>
              {open ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
            </Box>
            <Collapse in={open}>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {rows.map(({ key, val }) => (
                      <TableRow key={key}>
                        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 'bold', py: 0.5, pl: 1.5, width: '45%' }}>
                          {key}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', fontFamily: 'monospace', py: 0.5, wordBreak: 'break-word' }}>
                          {val}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        ) : (
          // Single value: inline label + value
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', flexShrink: 0 }}>
              {item.label}:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary', wordBreak: 'break-word' }}>
              {item.value}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InfoSidePanel({ ticket, isProcessing, onClose, streamingLogs = [] }) {
  const [ticketDataExpanded, setTicketDataExpanded] = useState(true)

  // Merge live streaming log entries into the ticket so buildFactList sees them in real time.
  // streamingLogs are ExecutionEntry-shaped objects (same shape as ticket.logs[]).
  // Deduplicate: filter out streaming entries already present in any log (parent or child) by timestamp.
  const existingTimestamps = new Set(getAllLogs(ticket).map(l => l.timestamp))
  const newStreamingLogs = streamingLogs.filter(l => !existingTimestamps.has(l.timestamp))
  const mergedTicket = newStreamingLogs.length > 0
    ? { ...ticket, logs: [...(ticket?.logs || []), ...newStreamingLogs] }
    : ticket
  const facts = buildFactList(mergedTicket)

  const ticketData = ticket?.data || {}
  const showLive = isProcessing

  const arrayGroups = Object.entries(ticketData).filter(
    ([, v]) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object'
  )
  const hasTicketData = arrayGroups.length > 0

  return (
    <Paper
      elevation={0}
      sx={{
        width: 340, flexShrink: 0,
        height: '100%',
        borderRadius: 0,
        display: 'flex', flexDirection: 'column',
        borderLeft: 1, borderColor: 'divider', overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0,
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Agent Info</Typography>
        <IconButton size="small" onClick={onClose} aria-label="close info panel">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

        {/* Live Activity */}
        {showLive && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <Circle sx={{
                fontSize: 8, color: 'success.main',
                animation: 'infoPanelPulse 1.5s ease-in-out infinite',
                '@keyframes infoPanelPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
              }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                Live Activity
              </Typography>
            </Box>
            <Divider sx={{ mt: 0.75 }} />
          </Box>
        )}

        {/* Ticket Info */}
        {hasTicketData && (
          <Box>
            <Accordion
              disableGutters elevation={0}
              expanded={ticketDataExpanded}
              onChange={(_, expanded) => setTicketDataExpanded(expanded)}
              sx={{
                bgcolor: 'transparent', border: 1, borderColor: 'divider',
                borderRadius: '4px !important', '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ fontSize: 16 }} />}
                sx={{ minHeight: 36, px: 1.5, '& .MuiAccordionSummary-content': { my: 0.75 } }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Ticket Info</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
                {arrayGroups.map(([key, items]) => (
                  <Box key={key} sx={{ px: 1.5, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {items.length > 1 && (
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {formatPropertyName(key)}
                      </Typography>
                    )}
                    {items.map((item, i) => (
                      <CollapsibleItem key={i} label={getDocumentLabel(item, i)} data={item} />
                    ))}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Facts Timeline */}
        {facts.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
              Information Gathered
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {facts.filter(item => item.type === 'fact').map((item, i, arr) =>
                <FactEntry key={`${item.timestamp}-${i}`} item={item} isLast={i === arr.length - 1} />
              )}
            </Box>
          </Box>
        )}

        {/* Empty state */}
        {!showLive && !hasTicketData && facts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">No information gathered yet.</Typography>
            <Typography variant="caption" color="text.secondary">
              Data will appear as the agent processes the ticket.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  )
}
